import { useAuth, useUser, useSignIn, useSignUp, useClerk } from '@clerk/clerk-react';
import { useState } from 'react';

export const useClerkAuth = () => {
    const { isLoaded: authLoaded, userId, sessionId, getToken } = useAuth();
    const { isLoaded: userLoaded, isSignedIn, user } = useUser();
    const { signIn: clerkSignIn, isLoaded: signInLoaded } = useSignIn();
    const { signUp: clerkSignUp, isLoaded: signUpLoaded } = useSignUp();
    const { signOut: clerkSignOut } = useClerk();
    const [error, setError] = useState('');

    // Combine loading states
    // Only wait for signIn/signUp to load if not already signed in (they're only needed for auth flows)
    const loading = !authLoaded || !userLoaded || (!isSignedIn && (!signInLoaded || !signUpLoaded));
    const initDone = authLoaded && userLoaded;

    const signIn = async (email, password) => {
        try {
            setError('');
            if (!clerkSignIn) throw new Error('Sign in not initialized');
            
            const result = await clerkSignIn.create({
                identifier: email,
                password,
            });

            if (result.status === 'complete') {
                await clerkSignIn.setActive({ session: result.createdSessionId });
                return { success: true, user: result };
            } else {
                // Handle 2FA or other additional steps if needed
                return { success: false, error: 'Additional verification required' };
            }
        } catch (err) {
            console.error('Sign in error:', err);
            const errorMessage = err.errors?.[0]?.message || err.message || 'Sign in failed';
            setError(errorMessage);
            return { success: false, error: errorMessage };
        }
    };

    const signUp = async (email, password) => {
        try {
            setError('');
            if (!clerkSignUp) throw new Error('Sign up not initialized');
            
            const result = await clerkSignUp.create({
                emailAddress: email,
                password,
            });

            if (result.status === 'complete') {
                await clerkSignUp.setActive({ session: result.createdSessionId });
                return { success: true, user: result };
            } else if (result.status === 'missing_requirements') {
                // Email verification might be required
                await result.prepareEmailAddressVerification({ strategy: 'email_code' });
                return { 
                    success: false, 
                    error: 'Please check your email for verification code',
                    requiresVerification: true,
                    signUpAttempt: result
                };
            }
        } catch (err) {
            console.error('Sign up error:', err);
            const errorMessage = err.errors?.[0]?.message || err.message || 'Sign up failed';
            setError(errorMessage);
            return { success: false, error: errorMessage };
        }
    };

    const logout = async () => {
        try {
            setError('');
            await clerkSignOut();
            return { success: true };
        } catch (err) {
            console.error('Logout error:', err);
            const errorMessage = err.message || 'Logout failed';
            setError(errorMessage);
            return { success: false, error: errorMessage };
        }
    };

    // Get session token for API calls
    const getSessionToken = async () => {
        try {
            // Check if user is signed in first
            if (!isSignedIn) {
                console.log('User not signed in, cannot get token');
                return null;
            }
            
            // Get a fresh JWT token from Clerk without template
            const token = await getToken();
            if (!token) {
                console.error('No token returned from Clerk');
            }
            return token;
        } catch (err) {
            console.error('Error getting session token:', err);
            return null;
        }
    };

    // Map Clerk user object to match expected user structure
    const mappedUser = user ? {
        uid: user.id,
        email: user.primaryEmailAddress?.emailAddress,
        emailVerified: user.primaryEmailAddress?.verification?.status === 'verified',
        displayName: user.fullName || user.firstName || user.username,
        photoURL: user.imageUrl,
        // Add any other fields your app uses
    } : null;

    return {
        user: mappedUser,
        loading,
        initDone,
        error,
        signIn,
        signUp,
        logout,
        getSessionToken,
        // Additional Clerk-specific helpers
        isSignedIn,
        userId,
        sessionId,
        rawUser: user, // Original Clerk user object if needed
    };
};